import { Component, type ErrorInfo, type ReactNode } from 'react';
import { reportClientError, stripUrlFragment } from '../telemetry/report-client-error.js';
import type { ClientErrorReport } from '../dto/client-error.js';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Which portal caught the error — carried through to the log line. */
  portal: ClientErrorReport['portal'];
  apiBaseUrl: string;
  /** SIL-1582: synchronous current-token source for crash attribution. */
  getToken?: () => string | null;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Root-of-tree React ErrorBoundary (SIL-666). Catches render-time errors
 * anywhere below it, reports them to POST /api/client-error, and renders a
 * real fallback instead of the white screen the app previously showed on
 * any uncaught render error.
 *
 * Does NOT catch: errors in event handlers (React never routes those
 * through componentDidCatch) or errors thrown while rendering the fallback
 * itself. Route-level failures during data loading/rendering are instead
 * caught by TanStack Router's own `errorComponent` (see each app's
 * `routes/__root.tsx`), which reports through this same
 * `reportClientError` call with `source: 'router-error'`.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    reportClientError(this.props.apiBaseUrl, {
      message: error.message || String(error),
      name: error.name,
      stack: error.stack,
      componentStack: info.componentStack ?? undefined,
      url: typeof window !== 'undefined' ? stripUrlFragment(window.location.href) : undefined,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      portal: this.props.portal,
      source: 'error-boundary',
    }, this.props.getToken);
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  override render(): ReactNode {
    if (this.state.error) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background p-8 text-center">
          <h1 className="text-lg font-semibold text-foreground">Something went wrong</h1>
          <p className="max-w-md text-sm text-muted-foreground">
            An unexpected error occurred and the team has been notified. Try reloading the
            page — if the problem persists, contact support.
          </p>
          <button
            type="button"
            className="rounded-md bg-brand-50 px-4 py-2 text-sm font-medium text-brand-700 hover:bg-brand-100"
            onClick={this.handleReload}
          >
            Reload page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
